import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getList } from "../../services/service";
import { searchAkfaPositions } from "../../services/akfa/akfaService";

export const ARTICLE_GROUP_TYPES = ["PROFILES", "ACCESSORIES", "SEAL", "FILLINGS"];

const SEARCH_DEBOUNCE_MS = 400;

const targetKey = (t) => `${t.productId}-${t.textureId}`;

// WinPro pozitsiyalarini (mahsulot + tekstura) qidirish va tanlash — GET /v1/akfa/positions/search
export default function AkfaPositionPicker({ akfaProductId, series, targets, onChange }) {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);

    const [query, setQuery] = useState("");
    const [articleGroupType, setArticleGroupType] = useState("");
    const [productSeriesId, setProductSeriesId] = useState("");
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    // positions/search faqat narxi bor teksturalarni qaytaradi — qolganlari shu yerdan qo'shiladi
    const [allTextures, setAllTextures] = useState({}); // { [productId]: { loading, list } }

    useEffect(() => {
        const q = query.trim();
        if (!q && !articleGroupType && !productSeriesId) {
            setResults([]);
            return;
        }
        let cancelled = false;
        const timeout = setTimeout(async () => {
            setIsSearching(true);
            const response = await searchAkfaPositions(token, { q, articleGroupType, productSeriesId });
            if (cancelled) return;
            setResults(response.success ? response.data || [] : []);
            setIsSearching(false);
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, articleGroupType, productSeriesId]);

    const loadAllTextures = async (productId) => {
        setAllTextures((prev) => ({ ...prev, [productId]: { loading: true, list: [] } }));
        const response = await getList(
            `${process.env.REACT_APP_HOST_BASE_URL}/v1/texture/list/by/productId/${productId}`,
            token
        );
        setAllTextures((prev) => ({
            ...prev,
            [productId]: { loading: false, list: response.success ? response.data || [] : [] },
        }));
    };

    // Natijalarni mahsulot bo'yicha guruhlash + "barcha teksturalar"dan narxsizlarini qo'shish
    const groups = [];
    const groupIndex = {};
    results.forEach((pos) => {
        if (groupIndex[pos.productId] === undefined) {
            groupIndex[pos.productId] = groups.length;
            groups.push({ productId: pos.productId, productName: pos.productName, article: pos.article, rows: [] });
        }
        groups[groupIndex[pos.productId]].rows.push(pos);
    });
    groups.forEach((group) => {
        const extra = allTextures[group.productId]?.list || [];
        const known = new Set(group.rows.map((r) => r.textureId));
        extra
            .filter((tx) => !known.has(tx.id))
            .forEach((tx) =>
                group.rows.push({
                    productId: group.productId,
                    productName: group.productName,
                    article: group.article,
                    textureId: tx.id,
                    textureName: tx.name,
                    price: null,
                    withoutPrice: true,
                })
            );
    });

    const selectedKeys = new Set(targets.map(targetKey));

    const isLockedByOther = (pos) => pos.mappedToAkfaProductId && pos.mappedToAkfaProductId !== akfaProductId;

    const toggle = (pos) => {
        if (isLockedByOther(pos)) return;
        const key = targetKey(pos);
        if (selectedKeys.has(key)) {
            onChange(targets.filter((tgt) => targetKey(tgt) !== key));
        } else {
            onChange([...targets, pos]);
        }
    };

    const inputClass =
        "border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500";

    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("akfaIntegrationPage.modal.targets_label")}
            </label>
            {targets.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-3">
                    {targets.map((tgt) => (
                        <span
                            key={targetKey(tgt)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium"
                        >
                            {tgt.productName} · {tgt.textureName}
                            <button type="button" onClick={() => toggle(tgt)} className="hover:text-indigo-900">
                                <XMarkIcon className="w-3.5 h-3.5" />
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-gray-500 mb-3">{t("akfaIntegrationPage.modal.no_targets_yet")}</p>
            )}

            <div className="flex flex-wrap gap-2 mb-2">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("akfaIntegrationPage.modal.search_product")}
                    className={`${inputClass} flex-1 min-w-[200px]`}
                />
                <select
                    value={articleGroupType}
                    onChange={(e) => setArticleGroupType(e.target.value)}
                    className={`${inputClass} w-[160px]`}
                >
                    <option value="">{t("akfaIntegrationPage.modal.all_group_types")}</option>
                    {ARTICLE_GROUP_TYPES.map((type) => (
                        <option key={type} value={type}>
                            {t(type)}
                        </option>
                    ))}
                </select>
                <select
                    value={productSeriesId}
                    onChange={(e) => setProductSeriesId(e.target.value)}
                    className={`${inputClass} w-[160px]`}
                >
                    <option value="">{t("akfaIntegrationPage.modal.all_series")}</option>
                    {series.map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
                {isSearching ? (
                    <p className="p-3 text-sm text-gray-500">{t("akfaIntegrationPage.common.loading")}</p>
                ) : results.length === 0 ? (
                    <p className="p-3 text-sm text-gray-500">
                        {query || articleGroupType || productSeriesId
                            ? t("akfaIntegrationPage.modal.no_positions_found")
                            : t("akfaIntegrationPage.modal.search_hint")}
                    </p>
                ) : (
                    groups.map((group) => {
                        const texturesState = allTextures[group.productId];
                        return (
                            <div key={group.productId} className="border-b last:border-b-0">
                                <div className="flex items-center justify-between gap-3 px-3 py-1.5 bg-gray-50 text-sm">
                                    <span>
                                        <span className="font-medium text-gray-900">{group.productName}</span>
                                        {group.article && <span className="text-gray-400"> ({group.article})</span>}
                                    </span>
                                    {!texturesState?.list?.length && (
                                        <button
                                            type="button"
                                            onClick={() => loadAllTextures(group.productId)}
                                            disabled={texturesState?.loading}
                                            className="text-xs text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                                        >
                                            {texturesState?.loading
                                                ? t("akfaIntegrationPage.common.loading")
                                                : t("akfaIntegrationPage.modal.show_all_textures")}
                                        </button>
                                    )}
                                </div>
                                {group.rows.map((pos) => {
                                    const locked = isLockedByOther(pos);
                                    return (
                                        <label
                                            key={targetKey(pos)}
                                            className={`flex items-center justify-between gap-3 pl-6 pr-3 py-2 border-t border-gray-100 text-sm ${
                                                locked
                                                    ? "bg-gray-50 text-gray-400 cursor-not-allowed"
                                                    : "cursor-pointer hover:bg-gray-50"
                                            }`}
                                        >
                                            <span className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    disabled={locked}
                                                    checked={selectedKeys.has(targetKey(pos))}
                                                    onChange={() => toggle(pos)}
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                                                />
                                                {pos.textureName}
                                            </span>
                                            <span className="text-xs text-right">
                                                {locked
                                                    ? t("akfaIntegrationPage.modal.linked_to", { name: pos.mappedToAkfaName })
                                                    : pos.withoutPrice
                                                    ? <span className="text-yellow-700">{t("akfaIntegrationPage.modal.no_price_yet")}</span>
                                                    : pos.price != null
                                                    ? `${pos.price} ${pos.currencySymbol || ""}`
                                                    : "—"}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        );
                    })
                )}
            </div>
            <p className="mt-1 text-xs text-gray-400">{t("akfaIntegrationPage.modal.search_limit_hint")}</p>
        </div>
    );
}
